import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
    return (
        <span className={`block overflow-hidden pb-1 ${className}`}>
            <motion.span
                className="block will-change-transform"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const { t } = useLanguage();

    return (
        <section id="hero" data-testid="hero-section" className="relative overflow-hidden">
            <div className="absolute inset-0" aria-hidden="true">
                <img
                    src="/hero-bg.png"
                    alt=""
                    data-testid="hero-background"
                    className="h-full w-full scale-105 object-cover blur-[2px]"
                />
                <div className="absolute inset-0 bg-[#0B1F33]/[0.78]" />
            </div>
            <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-6 pb-20 pt-32 sm:pt-36 lg:grid-cols-2 lg:px-10 lg:pb-28 lg:pt-40">
                <div>
                    <h1
                        data-testid="hero-name"
                        className="mt-8 font-display text-5xl font-bold leading-[1.02] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl"
                    >
                        <MaskedLine delay={0.25}>{t.hero.title}</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
                        data-testid="hero-subtitle"
                        className="mt-7 font-mono text-sm font-semibold uppercase tracking-[0.25em] text-sky-400 sm:text-base"
                    >
                        {t.hero.subtitle}
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65, duration: 0.6, ease: EASE }}
                        data-testid="hero-credentials"
                        className="mt-4 font-mono text-sm font-semibold uppercase tracking-[0.25em] text-sky-400 sm:text-base"
                    >
                        {t.hero.credentials}
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.67, duration: 0.6, ease: EASE }}
                        data-testid="hero-text"
                        className="mt-6 max-w-xl text-base font-medium leading-relaxed text-slate-100 sm:text-lg"
                    >
                        {t.hero.text}
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.73, duration: 0.6, ease: EASE }}
                        data-testid="hero-text-2"
                        className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base"
                    >
                        {t.hero.text2}
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.79, duration: 0.6, ease: EASE }}
                        data-testid="hero-experience"
                        className="mt-5 max-w-xl border-l-2 border-sky-400 pl-4 text-sm leading-relaxed text-slate-400"
                    >
                        {t.hero.experience}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.91, duration: 0.6, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#contatti"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2 bg-sky-400 px-8 py-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-sky-500"
                        >
                            {t.hero.ctaPrimary}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href="#servizi"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-2 border border-navy-600 px-8 py-4 font-mono text-xs font-bold uppercase tracking-widest text-slate-50 transition-colors hover:border-sky-400 hover:text-sky-400"
                        >
                            {t.hero.ctaSecondary}
                        </a>
                    </motion.div>
                </div>

                <div className="flex items-center justify-center">
                    <motion.img
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.8, ease: EASE }}
                        src="/gianluca-capri.png"
                        alt="Gianluca Capri — Senior IT & MES Project Manager"
                        data-testid="hero-portrait"
                        className="h-auto w-1/2"
                    />
                </div>
            </div>
        </section>
    );
}
